<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75" />

                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                        enter-from="translate-x-full" enter-to="translate-x-0"
                        leave="transform transition ease-in-out duration-500 sm:duration-700" leave-from="translate-x-0"
                        leave-to="translate-x-full">
                        <div class="w-screen max-w-3xl">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4 bg-amaze-coral sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white font-bold">
                                                Confirm Offer
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                    class="bg-amaze-coral rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                    @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true" />
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-white">
                                                You are confirming quote `{{ quote.name }}`
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true" />
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6 space-y-6">

                                            <div class="grid grid-cols-12 gap-4 pt-6 pb-5">

                                                <h2 class="col-span-12 text-lg text-bold">Offer Information</h2>

                                                <div class="col-span-12">
                                                    <table class="w-full divide-y divide-gray-200">
                                                        <thead class="bg-gray-50">
                                                            <tr>
                                                                <th scope="col"
                                                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                    Name
                                                                </th>
                                                                <th scope="col"
                                                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                    Total Selling Price
                                                                </th>
                                                                <th scope="col"
                                                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                    Total Buying Price
                                                                </th>
                                                                <th scope="col"
                                                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                    Profit
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr class="bg-white">
                                                                <td
                                                                    class="px-6 py-4 whitespace-nowrap text-xs font-medium text-gray-900">
                                                                    {{ quote.name ?? '' }}</td>
                                                                <td
                                                                    class="px-6 py-4 whitespace-nowrap text-xs font-medium text-gray-900">
                                                                    {{
    number_format(parseFloat(quote.subtotals.gross_sell_total).toFixed(2))
                                                                    }} (gross) /
                                                                    {{
                                                                    number_format(parseFloat(quote.subtotals.net_sell_total).toFixed(2))}}
                                                                    (net)</td>
                                                                <td
                                                                    class="px-6 py-4 whitespace-nowrap text-xs font-medium text-gray-900">
                                                                    {{
    number_format(parseFloat(quote.subtotals.gross_buy_total).toFixed(2))
                                                                    }} (gross) /
                                                                    {{
                                                                    number_format(parseFloat(quote.subtotals.net_buy_total).toFixed(2))}}
                                                                    (net)</td>
                                                                <td
                                                                    class="px-6 py-4 whitespace-nowrap text-xs font-medium text-gray-900">
                                                                    {{
                                                                    number_format(parseFloat(quote.subtotals.gross_profit_total).toFixed(2))}}
                                                                    (gross) /
                                                                    {{
                                                                    number_format(parseFloat(quote.subtotals.net_profit_total).toFixed(2))}}
                                                                    (net)</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>

                                                </div>
                                            </div>
                                            <div class="grid grid-cols-12 gap-4 pt-6 pb-5">

                                                <h2 class="col-span-12 text-lg text-bold">Proposal</h2>

                                                <div class="col-span-12">
                                                    <label for="proposed_at"
                                                        class="block text-sm text-gray-900 required">
                                                        Proposal Date
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="date" id="proposed_at" v-model="form.proposed_at"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{ 'border-red-500': form.errors.proposed_at }" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="grid grid-cols-12 gap-4 pt-6 pb-5">

                                                <h2 class="col-span-12 text-lg text-bold">Deposit Structure</h2>
                                                <div class="grid grid-cols-9 gap-4 col-span-12">
                                                    <div class="col-span-4">
                                                        <label for="deposit_price" v-if="index === 0"
                                                            class="block text-sm text-gray-900">
                                                            Amount
                                                        </label>
                                                    </div>
                                                    <div class="col-span-4">
                                                        <label for="deposit_date" v-if="index === 0"
                                                            class="block text-sm text-gray-900">
                                                            Date
                                                        </label>    
                                                    </div>
                                                    <div class="col-span-1"></div>
                                                </div>
                                                <div class="grid grid-cols-9 gap-4 col-span-12" :key="index"
                                                    v-for="(deposit, index) in form.deposits">
                                                    <div class="col-span-4">
                                            
                                                        <div class="mt-1">
                                                            <input type="text" id="deposit_price"
                                                                v-model="deposit.amount"
                                                                class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.deposits }" />
                                                        </div>
                                                    </div>
                                                    <div class="col-span-4">
                                                      
                                                        <div class="mt-1">
                                                            <input type="date" id="due_at" v-model="deposit.due_at"
                                                                class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{ 'border-red-500': form.errors.deposits }" />
                                                        </div>
                                                    </div>
                                                    <div class="col-span-1 flex">
                                                        <div class="mt-1 flex items-center justify-center" v-if="index+1 === form.deposits.length">
                                                            <MinusSmIcon class="h-4 w-4 mr-4" v-if="index+1 !== 1" @click="removeDepositRow(index)">
                                                            </MinusSmIcon>
                                                            <PlusSmIcon class="h-4 w-4" @click="addDepositsRow()">
                                                            </PlusSmIcon>
                                                           
                                                        </div>
                                                        <div class="mt-1 flex items-center justify-center" v-else>
                                                            <MinusSmIcon class="h-4 w-4" @click="removeDepositRow(index)">
                                                            </MinusSmIcon>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                        class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        @click="close()">
                                        Close
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                        class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-amaze-coral hover:bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                        <span v-if="updating === true">Update</span>
                                        <span v-else>Save</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import { ref, reactive } from 'vue'
import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { XIcon } from '@heroicons/vue/outline'
import { LinkIcon, PlusSmIcon, QuestionMarkCircleIcon, ExclamationCircleIcon, MinusSmIcon } from '@heroicons/vue/solid'
import { useForm } from '@inertiajs/vue3'
import Inertia from '@inertiajs/vue3'
import SlideOutErrors from "@/Components/SlideOutErrors.vue";
import { emitter } from "@/eventHub";
import {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxLabel,
    ComboboxOption,
    ComboboxOptions
} from '@headlessui/vue'
import { mapState } from 'vuex'
import moment from 'moment'

export default {
    components: {
    SlideOutErrors,
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    LinkIcon,
    PlusSmIcon,
    QuestionMarkCircleIcon,
    ExclamationCircleIcon,
    XIcon,
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxLabel,
    ComboboxOption,
    ComboboxOptions,
    MinusSmIcon
},
    props: {
        project: Object,
        quote: Object
    },
    computed: {

    },
    watch: {

    },
    data() {
        return {
            show: ref(false),
            showErrors: ref(false),
            updating: false,
            form: useForm({
                deposits: [{ amount: 0, due_at: moment().format('YYYY-MM-DD')  }],
                proposed_at: null,
            })
        }
    },
    mounted() {

        // Open the confirm quote slideout
        emitter.on("openConfirmQuoteSlideout", () => {
            this.updating = false;
            this.open();
        })

    },
    methods: {
        addDepositsRow() {
            this.form.deposits.push({ amount: 0, due_at: moment().format('YYYY-MM-DD') });
        },
        removeDepositRow(index) {
            this.form.deposits.splice(index, 1);
        },
        submit() {
            this.form.post(route('frontend.project.quote.confirm', [this.project.id, this.quote.id]), {
                onSuccess: page => {
                    this.show = false;
                    this.showErrors = false;
                    this.form.reset();
                },
                onError: errors => {
                    this.showErrors = true;
                },
                preserveScroll: true,
                preserveState: false
            });

        },
        number_format(nStr) {
            nStr += '';
            let x = nStr.split('.');
            let x1 = x[0];
            let x2 = x.length > 1 ? '.' + x[1] : '';
            let rgx = /(\d+)(\d{3})/;
            while (rgx.test(x1)) {
                x1 = x1.replace(rgx, '$1' + ',' + '$2');
            }
            return x1 + x2;
        },
        doRounding(value, decimals) {
            return Number(Math.round(value + "e" + decimals) + "e-" + decimals).toFixed(2);
        },

        close() {
            this.form.reset();
            this.form.clearErrors();
            this.show = false;
        },

        open() {
            this.form.clearErrors();
            this.show = true;
        }
    }

}
</script>
